import { useEffect } from 'react';
import useSiteSettings from '@/lib/useSiteSettings';
import { useLang } from '@/lib/i18n';

/**
 * BrandHead — applies the gallery's brand settings to the document <head>:
 *   • favicon (link[rel="icon"]) — swapped at runtime
 *   • default <title> when no page-specific title is set
 *   • default <meta name="description">
 *   • Open Graph image + title + description for social previews
 *   • og:locale + JSON-LD @language reflecting the active route locale
 *
 * Locale-aware: on /fr/* routes, prefers `brand.<field>_fr` when a French
 * translation is supplied via the CMS. English pages remain unchanged.
 * Pages are still free to override <title> + <meta description> via
 * useDocumentMeta — this component sets sensible site-wide defaults that fall
 * through whenever the page hook does not provide its own values.
 */
export default function BrandHead() {
    const settings = useSiteSettings();
    const brand = settings?.brand;
    const { lang } = useLang();

    useEffect(() => {
        if (!brand) return;

        /* Pick a locale-appropriate value: for French pages, prefer the
           `<key>_fr` companion only when it is a non-empty string —
           otherwise fall through to the English value. This guarantees a
           French page never falls back to English chrome silently at the
           page level (that gate is enforced by the publication rules on
           long-form CMS content), while global brand chrome remains
           consistent when no localised copy has been supplied yet. */
        const pick = (key) => {
            if (lang === 'fr') {
                const v = brand[`${key}_fr`];
                if (typeof v === 'string' && v.trim()) return v;
            }
            return brand[key];
        };

        // Favicon — replace the existing <link rel="icon"> if a custom one is set
        if (brand.favicon) {
            let link = document.querySelector('link[rel="icon"]');
            if (!link) {
                link = document.createElement('link');
                link.setAttribute('rel', 'icon');
                document.head.appendChild(link);
            }
            const ext = brand.favicon.split('.').pop().toLowerCase().split('?')[0];
            const typeMap = { svg: 'image/svg+xml', ico: 'image/x-icon', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg' };
            if (typeMap[ext]) link.setAttribute('type', typeMap[ext]);
            link.setAttribute('href', brand.favicon);
        }

        // Default site title — applied when the page hasn't supplied its own.
        const PLACEHOLDER_TITLES = [
            'Maison Touré · Fine Contemporary African Art',
            'Maison Touré · Fine Contemporary African Art Gallery, Sydney',
        ];
        const browserTitle = pick('browser_title');
        if (browserTitle && PLACEHOLDER_TITLES.includes(document.title)) {
            document.title = browserTitle;
        }

        // Default meta description — overwrite the index.html placeholder values.
        const PLACEHOLDER_DESCS = [
            'A product of emergent.sh',
            'Maison Touré is a Sydney-based gallery dedicated to contemporary African art. Curated works, private viewings, provenance-led.',
            '',
        ];
        const metaDesc = pick('meta_description');
        if (metaDesc) {
            const metaEls = Array.from(document.querySelectorAll('meta[name="description"]'));
            if (metaEls.length === 0) {
                const meta = document.createElement('meta');
                meta.setAttribute('name', 'description');
                meta.setAttribute('content', metaDesc);
                document.head.appendChild(meta);
            } else {
                metaEls.forEach((m) => {
                    const cur = m.getAttribute('content') || '';
                    if (PLACEHOLDER_DESCS.includes(cur)) m.setAttribute('content', metaDesc);
                });
            }
        }

        // Open Graph & Twitter card
        const setMeta = (attr, key, value) => {
            if (!value) return;
            let el = document.querySelector(`meta[${attr}="${key}"]`);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            el.setAttribute('content', value);
        };

        setMeta('property', 'og:title', browserTitle);
        setMeta('property', 'og:description', metaDesc);
        setMeta('property', 'og:image', brand.og_image);
        setMeta('property', 'og:type', 'website');
        setMeta('property', 'og:locale', lang === 'fr' ? 'fr_FR' : 'en_AU');
        if (brand.og_image) setMeta('name', 'twitter:card', 'summary_large_image');
        setMeta('name', 'twitter:title', browserTitle);
        setMeta('name', 'twitter:description', metaDesc);
        setMeta('name', 'twitter:image', brand.og_image);

        // JSON-LD — the site-level Organization / ArtGallery / WebSite graph
        // is declared statically in `public/index.html` (id="ld-site-static")
        // so that Google sees the brand identity, logo and canonical URL on
        // first byte without waiting for JS. To avoid emitting a duplicate
        // WebSite entity at runtime we short-circuit here whenever the
        // static block is present.
        if (document.getElementById('ld-site-static')) {
            return;
        }
        const LD_ID = 'ld-site-locale';
        let ld = document.getElementById(LD_ID);
        if (!ld) {
            ld = document.createElement('script');
            ld.type = 'application/ld+json';
            ld.id = LD_ID;
            document.head.appendChild(ld);
        }
        ld.textContent = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: browserTitle || 'Maison Touré',
            inLanguage: lang === 'fr' ? 'fr' : 'en',
            url: (typeof window !== 'undefined' ? window.location.origin : '') + (lang === 'fr' ? '/fr' : '/'),
        });
    }, [brand, lang]);

    return null;
}
